import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Star } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface MarketDemandSkillsProps {
  skills: {
    name: string;
    demand: 'High' | 'Medium' | 'Low';
    growth: number;
  }[];
}

const MarketDemandSkills: React.FC<MarketDemandSkillsProps> = ({ skills }) => {
  const getDemandColor = (demand: string) => {
    switch (demand) {
      case 'High':
        return 'bg-green-100 text-green-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'Low':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const data = skills.map(skill => ({
    name: skill.name,
    growth: skill.growth,
    demand: skill.demand
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-6 rounded-xl shadow-md"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Market Demand Skills</h3>
        <TrendingUp className="h-6 w-6 text-primary-600" />
      </div>
      
      <div className="h-64 mb-6">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="growth" fill="#3B82F6" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="space-y-3">
        {skills.map((skill, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <div className="font-medium text-gray-900">{skill.name}</div>
              <div className="flex items-center mt-1">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDemandColor(skill.demand)}`}>
                  {skill.demand} Demand
                </span>
                <span className="ml-2 text-sm text-gray-500 flex items-center">
                  <TrendingUp className="h-4 w-4 mr-1 text-green-500" />
                  {skill.growth}% growth
                </span>
              </div>
            </div>
            <div className="flex items-center">
              {Array.from({ length: Math.round(skill.growth / 20) }).map((_, i) => (
                <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default MarketDemandSkills;